import { useEffect, useState } from "react";
import productService from "../services/productService";
import CustomSpinner from "./CustomSpinner";
import { PIECEWORK_TYPES } from "../constants/business";

const initialFields = { name: "", catalogType: "Producto terminado", unit: "pieza", color: "", stock: "", price: "", pieceworkType: "", recipe: [], description: "" };
const fieldsConfig = [
    ["name", "Nombre*", "text"], ["unit", "Unidad*", "text"], ["color", "Color", "text"],
    ["stock", "Existencia inicial*", "number"], ["price", "Precio", "number"],
];

const parseRecipe = (recipe) => {
    try { return Array.isArray(recipe) ? recipe : JSON.parse(recipe || "[]"); }
    catch { return []; }
};

export default function ProductForm({ onSuccess, product, products = [], show, setShow, isEdit }) {
    const [fields, setFields] = useState(initialFields);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFields({
            name: product?.name || "", catalogType: product?.catalogType || "Producto terminado", unit: product?.unit || "pieza",
            color: product?.color || "", stock: product?.stock || "", price: product?.price || "",
            pieceworkType: product?.pieceworkType || "",
            recipe: parseRecipe(product?.recipe), description: product?.description || "",
        });
    }, [product]);

    const close = () => setShow(false);
    const update = (field) => (event) => setFields({ ...fields, [field]: event.target.value });
    const recipeProducts = products.filter(({ catalogType }) => ["Resina", "Accesorio"].includes(catalogType));
    const updateRecipe = (index, field) => (event) => setFields({
        ...fields,
        recipe: fields.recipe.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: event.target.value } : item),
    });
    const addRecipeItem = () => setFields({ ...fields, recipe: [...fields.recipe, { productId: "", quantity: "" }] });
    const removeRecipeItem = (index) => setFields({ ...fields, recipe: fields.recipe.filter((_, itemIndex) => itemIndex !== index) });
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!fields.name.trim() || fields.stock === "") return alert("Por favor, complete todos los campos requeridos.");
        if (fields.catalogType === "Producto terminado" && fields.recipe.some(({ productId, quantity }) => !productId || Number(quantity) <= 0)) {
            return alert("Complete el insumo y la cantidad de cada fila de la receta.");
        }
        if (fields.catalogType === "Producto terminado" && !fields.pieceworkType) return alert("Seleccione el tipo de destajo.");
        setLoading(true);
        try {
            const data = { ...fields, recipe: fields.catalogType === "Producto terminado" ? fields.recipe : [] };
            if (product && isEdit) await productService.update(product.id, data);
            else await productService.create(data);
            onSuccess(); setFields(initialFields); close();
        } catch (error) { alert(isEdit ? "Error updating product:" : "Error creating product:", error); }
        finally { setLoading(false); }
    };

    return <>
        {loading && <CustomSpinner />}
        {show && <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/60 p-4 sm:items-center" role="dialog" aria-modal="true">
            <div className="my-0 max-h-[calc(100dvh-2rem)] w-full max-w-4xl overflow-y-auto rounded-2xl border border-black/10 bg-slate-50 shadow-2xl dark:border-white/10 dark:bg-slate-900 sm:my-8">
                <div className="flex items-center justify-between border-b border-black/10 p-5 dark:border-white/10"><h2 className="text-lg font-semibold">{isEdit ? "Editar" : "Crear"} Producto</h2><button onClick={close} aria-label="Cerrar">✕</button></div>
                <form onSubmit={handleSubmit} className="p-5">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <label className="block text-left text-sm font-medium">
                            Tipo
                            <select className="form-control mt-2"
                                value={fields.catalogType} onChange={update("catalogType")}>
                                <option>Producto terminado</option>
                                <option>Resina</option>
                                <option>Accesorio</option></select></label>
                        {fieldsConfig.map(
                            ([field, label, type]) =>
                                <label key={field} className="block text-left text-sm font-medium">
                                    {label}
                                    <input className="form-control mt-2" type={type}
                                        disabled={isEdit && field === "stock"} value={fields[field]}
                                        onChange={update(field)} /></label>
                        )}
                        {fields.catalogType === "Producto terminado" && <label className="block text-left text-sm font-medium">
                            Tipo de destajo
                            <select className="form-control mt-2" value={fields.pieceworkType} onChange={update("pieceworkType")} required>
                                <option value="">Seleccionar tipo</option>
                                {PIECEWORK_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </label>}
                        {fields.catalogType === "Producto terminado" && <label className="block text-left text-sm font-medium xl:col-span-2">
                            Receta
                            <div className="mt-2 space-y-2">
                                {fields.recipe.map((item, index) => <div className="flex gap-2" key={index}>
                                    <select className="form-control" value={item.productId} onChange={updateRecipe(index, "productId")}>
                                        <option value="">Seleccionar insumo</option>
                                        {recipeProducts.map(({ id, name, unit }) => <option key={id} value={id}>{name} ({unit})</option>)}
                                    </select>
                                    <input className="form-control max-w-32" type="number" min="0.01" step="any" placeholder="Cantidad"
                                        value={item.quantity} onChange={updateRecipe(index, "quantity")} />
                                    <button type="button" className="btn btn-danger px-3" onClick={() => removeRecipeItem(index)} aria-label="Quitar insumo">✕</button>
                                </div>)}
                                <button type="button" className="btn border-slate-300 dark:border-slate-700" onClick={addRecipeItem}>Agregar insumo</button>
                            </div>
                        </label>}
                        <label className="block text-left text-sm font-medium xl:col-span-2">
                            Descripción
                            <textarea className="form-control mt-2 h-24" value={fields.description}
                                onChange={update("description")} />
                        </label>
                    </div>
                    <div className="mt-6 flex justify-end gap-2"><button type="button" className="btn border-slate-300 dark:border-slate-700" onClick={close}>Cerrar</button><button className="btn btn-info" type="submit">{isEdit ? "Actualizar" : "Agregar"}</button></div>
                </form>
            </div>
        </div>}
    </>;
}
